import { useEffect, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';

import {
	ISelectedImagesPreviewProps,
	ISelectedUploadItem,
} from 'src/features/patient_asset';

const getDisplayName = (item: ISelectedUploadItem): string =>
	item.dicomConversion?.sourceName || item.file.name;

const SelectedImagesPreview: React.FC<ISelectedImagesPreviewProps> = ({
	items,
	onRemove,
	disabled = false,
}) => {
	const [urlById, setUrlById] = useState<Record<string, string>>({});
	const urlByIdRef = useRef<Record<string, string>>({});

	useEffect(() => {
		const previous = urlByIdRef.current;
		const next: Record<string, string> = {};
		const nextIds = new Set(items.map((item) => item.id));

		items.forEach((item) => {
			if (previous[item.id]) {
				next[item.id] = previous[item.id];
			} else {
				next[item.id] = URL.createObjectURL(item.file);
			}
		});

		Object.entries(previous).forEach(([id, url]) => {
			if (!nextIds.has(id)) {
				URL.revokeObjectURL(url);
			}
		});

		urlByIdRef.current = next;
		setUrlById(next);
	}, [items]);

	useEffect(() => {
		return () => {
			Object.values(urlByIdRef.current).forEach((url) => {
				URL.revokeObjectURL(url);
			});
			urlByIdRef.current = {};
		};
	}, []);

	return (
		<Box
			sx={{
				border: 1,
				borderStyle: 'dashed',
				borderColor: 'grey.400',
				width: '100%',
				height: '100%',
				overflowY: 'auto',
				p: 1.5,
				boxSizing: 'border-box',
				display: 'flex',
				flexDirection: 'column',
				gap: 1,
			}}
		>
			{items.map((item) => {
				const previewUrl = urlById[item.id];
				const displayName = getDisplayName(item);

				return (
					<Box
						key={item.id}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1.5,
							border: 1,
							borderColor: 'grey.300',
							borderRadius: 1,
							p: 1,
							backgroundColor: '#fafafa',
							minHeight: 88,
						}}
					>
						<Box
							sx={{
								width: 72,
								height: 72,
								flexShrink: 0,
								borderRadius: 0.5,
								overflow: 'hidden',
								backgroundColor: '#eee',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							{previewUrl ? (
								<img
									src={previewUrl}
									alt={displayName}
									style={{
										width: '100%',
										height: '100%',
										objectFit: 'contain',
										pointerEvents: 'none',
										userSelect: 'none',
									}}
								/>
							) : null}
						</Box>

						<Box
							sx={{
								flex: 1,
								minWidth: 0,
								display: 'flex',
								flexDirection: 'column',
								gap: 0.25,
							}}
						>
							<Typography
								variant='body2'
								sx={{
									fontWeight: 500,
									wordBreak: 'break-word',
								}}
								title={displayName}
							>
								{displayName}
							</Typography>
							{item.dicomConversion && (
								<Typography
									variant='caption'
									sx={{
										color: 'text.secondary',
										wordBreak: 'break-word',
									}}
								>
									Converted to PNG ·{' '}
									{item.dicomConversion.width}×
									{item.dicomConversion.height} px
								</Typography>
							)}
						</Box>

						<IconButton
							size='small'
							disabled={disabled}
							onClick={() => onRemove(item.id)}
							aria-label={`Remove ${displayName}`}
							sx={{
								flexShrink: 0,
								width: 28,
								height: 28,
								backgroundColor: '#F5F5F5',
								':hover': {
									backgroundColor: '#E0E0E0',
								},
							}}
						>
							<CloseIcon sx={{ fontSize: 18 }} />
						</IconButton>
					</Box>
				);
			})}
		</Box>
	);
};

export default SelectedImagesPreview;
